import { Cake } from 'lucide-react'

import { dateOfBirthSchema, type PublicPatient } from '@snootsvet/shared/portal'

import {
  EditActions,
  EditableField,
  EditableValue,
  EditButton,
  useEditableField,
} from '@/customer/components/editable-field'
import { Input } from '@/customer/components/ui/input'
import { useRegion } from '@/customer/hooks/use-region'
import { useUpdatePet } from '@/customer/hooks/use-update-pet'
import { formatDob } from '@/customer/lib/formatters'

/**
 * Date of birth row. Editable at any time - members commonly record
 * a rough guess up front and refine after the first vet visit.
 * Empty input clears the value.
 */
export function DateOfBirthPetField({ pet }: { pet: PublicPatient }) {
  const region = useRegion()
  const updatePet = useUpdatePet(pet.id)
  const dobField = useEditableField({
    initialValue: pet.dateOfBirth ?? '',
    onSave: async ({ value, setError }) => {
      const next = value || null
      if (next === pet.dateOfBirth) return
      const parsed = dateOfBirthSchema.nullable().safeParse(next)
      if (!parsed.success) {
        setError(parsed.error.issues[0].message)
        return
      }
      try {
        await updatePet.mutateAsync({ dateOfBirth: next })
      } catch {
        setError('Something went wrong saving. Please try again.')
      }
    },
  })

  return (
    <EditableField
      label="Date of birth"
      icon={Cake}
      error={dobField.error}
      action={<EditButton field={dobField} label="date of birth" />}
    >
      {dobField.isEditing ? (
        <Input
          autoFocus
          type="date"
          variant="underline"
          value={dobField.draft}
          onChange={(e) => dobField.setDraft(e.target.value)}
        />
      ) : (
        <EditableValue>
          {pet.dateOfBirth ? (
            formatDob(pet.dateOfBirth, region)
          ) : (
            <span className="text-muted-foreground">Not set</span>
          )}
        </EditableValue>
      )}
      <EditActions field={dobField} />
    </EditableField>
  )
}
